import { config } from '../config';
import { ok, reject, type DomainResult } from './result';

/**
 * INV-4, INV-5, LOCK-4, LOCK-6, LOCK-7, LIVE-8 — `boardPosition` allocation.
 *
 * **The allocation rule, in one sentence (design §6.2): positions always form a contiguous prefix
 * `0..n−1`, and a new position is always allocated at the tail (`n`).**
 *
 * That rule is the only reason INV-11 and LOCK-7 are jointly satisfiable, and they are otherwise in
 * direct contradiction:
 *
 *   - INV-11 describes a **packed** render — active tiles in position order first, dismissed below.
 *   - LOCK-7 requires that filling an empty slot later "MUST NOT change the rendered slot of any
 *     active tile". Under packing with lowest-free-integer allocation, filling a *middle* gap
 *     shifts every later tile down one cell. LOCK-7 breached.
 *
 * Tail allocation removes middle gaps entirely, because the only composition changes LOCK-6 permits
 * are (a) the initial lock, which assigns `0..k−1` contiguously, (b) filling an under-full board,
 * which takes `n`, and (c) a swap, in which the incoming tile takes the outgoing tile's exact
 * position — creating no gap and moving nothing.
 *
 * Every function here returns the writes to apply, in the order to apply them, and never performs
 * them. A rejection carries its own OBS-1 entry (`result.ts`).
 */

const MODULE = 'board';

/** The only field this module reads or writes. INV-4 keeps it out of every capture handler. */
export interface PositionedRow {
  readonly id: string;
  readonly boardPosition: number | null;
}

/** One `boardPosition` write. `null` clears a position and returns the hypothesis to list-only. */
export interface PositionWrite {
  readonly id: string;
  readonly boardPosition: number | null;
}

export interface BoardShape {
  /** How many hypotheses currently hold a position. */
  readonly filled: number;
  /** LOCK-7 — a board with fewer than `BOARD_CAP` tiles is legal; these render as empty state. */
  readonly emptySlots: number;
}

function positionsOf(rows: readonly PositionedRow[]): number[] {
  return rows
    .map((row) => row.boardPosition)
    .filter((position): position is number => position !== null);
}

/**
 * INV-5 + design §6.2 — is this board's position set legal?
 *
 * Checks all four properties the allocation rule guarantees, because a caller that has drifted from
 * the rule must not be handed a tail index computed as if it had not: unique positions, at most
 * `BOARD_CAP`, each within `0..BOARD_CAP−1`, and a contiguous prefix with no middle gap.
 */
export function validateBoard(rows: readonly PositionedRow[]): DomainResult<BoardShape> {
  const positions = positionsOf(rows);

  if (positions.length > config.BOARD_CAP) {
    return reject(
      MODULE,
      null,
      'ERR_BOARD_CAP',
      `A board holds at most ${config.BOARD_CAP} tiles; this one has ${positions.length}.`,
      { filled: positions.length, cap: config.BOARD_CAP },
    );
  }

  const unique = new Set(positions);
  if (unique.size !== positions.length) {
    return reject(MODULE, null, 'ERR_BOARD_DUPLICATE', 'Two hypotheses hold the same board slot.', {
      positions: [...positions].sort((a, b) => a - b),
    });
  }

  for (let slot = 0; slot < positions.length; slot++) {
    if (!unique.has(slot)) {
      return reject(
        MODULE,
        null,
        'ERR_BOARD_NOT_CONTIGUOUS',
        `Board slots must be a contiguous run from 0; slot ${slot} is empty while later slots are filled.`,
        { positions: [...positions].sort((a, b) => a - b) },
      );
    }
  }

  return ok({ filled: positions.length, emptySlots: config.BOARD_CAP - positions.length });
}

/**
 * LOCK-4 confirm, and PRE-7 "start locked" — assign `0..k−1` in the confirmed order.
 *
 * INV-4: "assigned exactly once per lock or per explicit user swap". So this refuses to run against
 * a board that already holds positions. That is the row-level half of the guarantee whose
 * compile-time half is `canLock` refusing a second lock (`machine.ts`) — a UI guard is not an
 * invariant, and neither is a state flag on its own.
 *
 * PRE-7's other exit, "start in Discovery", assigns **no** position: candidates are created
 * list-only so the later LOCK-4 confirm is the first and only assignment (design §6.0).
 */
export function planLockPositions(
  orderedIds: readonly string[],
  existing: readonly PositionedRow[],
): DomainResult<PositionWrite[]> {
  if (orderedIds.length > config.BOARD_CAP) {
    return reject(
      MODULE,
      null,
      'ERR_BOARD_CAP',
      `You can lock at most ${config.BOARD_CAP} hypotheses; ${orderedIds.length} were confirmed.`,
      { requested: orderedIds.length, cap: config.BOARD_CAP },
    );
  }

  if (new Set(orderedIds).size !== orderedIds.length) {
    return reject(
      MODULE,
      null,
      'ERR_BOARD_DUPLICATE',
      'The same hypothesis was confirmed for two board slots.',
      { orderedIds: [...orderedIds] },
    );
  }

  const alreadyPositioned = existing.filter((row) => row.boardPosition !== null);
  if (alreadyPositioned.length > 0) {
    return reject(
      MODULE,
      null,
      'ERR_BOARD_ALREADY_POSITIONED',
      'This board is already locked; positions are assigned exactly once (INV-4).',
      { alreadyPositioned: alreadyPositioned.map((row) => row.id) },
    );
  }

  return ok(orderedIds.map((id, index) => ({ id, boardPosition: index })));
}

/**
 * LOCK-6 / LOCK-7 — fill an empty slot, from a Halftime swap-in or a FLAG-3 promotion.
 *
 * Allocates the **tail** index, so no active tile's rendered slot changes (LOCK-7). The board must
 * already be a contiguous prefix, which `validateBoard` establishes before the index is computed.
 *
 * LIVE-8's "no path may create a 9th positioned tile" is enforced here as a rejection and again by
 * the `&[matchId+boardPosition]` unique index plus an in-transaction postcondition (design §4,
 * INV-5). Three mechanisms, because this one is a hard cap the requirements state twice.
 */
export function planFillEmptySlot(
  existing: readonly PositionedRow[],
  incomingId: string,
): DomainResult<PositionWrite[]> {
  const shape = validateBoard(existing);
  if (!shape.ok) return shape;

  if (existing.some((row) => row.id === incomingId && row.boardPosition !== null)) {
    return reject(
      MODULE,
      null,
      'ERR_BOARD_ALREADY_POSITIONED',
      'That hypothesis already holds a board slot.',
      { incomingId },
    );
  }

  if (shape.value.filled >= config.BOARD_CAP) {
    return reject(
      MODULE,
      null,
      'ERR_BOARD_CAP',
      `The board is full at ${config.BOARD_CAP} tiles. Swap a tile out instead.`,
      { incomingId, cap: config.BOARD_CAP },
    );
  }

  return ok([{ id: incomingId, boardPosition: shape.value.filled }]);
}

/**
 * LOCK-6 / DD-3 — swap a tile out and another in, at the outgoing tile's exact position.
 *
 * **The outgoing position is nulled first.** IndexedDB enforces `&[matchId+boardPosition]`
 * uniqueness per `put`, so writing the incoming row while the outgoing one still holds the slot
 * throws `ConstraintError` even inside a single transaction (design §3.4). The ordering is part of
 * this plan, not left to the caller.
 *
 * "No displacement is ever silent" (LOCK-6) is the caller's obligation — the user names the tile
 * being displaced — but the plan makes the displacement explicit in the ops either way.
 */
export function planSwap(
  existing: readonly PositionedRow[],
  outgoingId: string,
  incomingId: string,
): DomainResult<PositionWrite[]> {
  if (outgoingId === incomingId) {
    return reject(
      MODULE,
      null,
      'ERR_BOARD_DUPLICATE',
      'A hypothesis cannot be swapped with itself.',
      { outgoingId },
    );
  }

  const outgoing = existing.find((row) => row.id === outgoingId);
  if (outgoing === undefined || outgoing.boardPosition === null) {
    return reject(
      MODULE,
      null,
      'ERR_BOARD_NOT_POSITIONED',
      'The tile being swapped out does not hold a board slot.',
      { outgoingId },
    );
  }

  if (existing.some((row) => row.id === incomingId && row.boardPosition !== null)) {
    return reject(
      MODULE,
      null,
      'ERR_BOARD_ALREADY_POSITIONED',
      'The tile being swapped in already holds a board slot.',
      { incomingId },
    );
  }

  return ok([
    { id: outgoingId, boardPosition: null },
    { id: incomingId, boardPosition: outgoing.boardPosition },
  ]);
}
